import React from 'react'

import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TableSortLabel from '@mui/material/TableSortLabel'

import Paper from '@mui/material/Paper'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import { styled } from '@mui/material/styles'
import { InputProps } from '@mui/material'
import Link from '@mui/material/Link'

import Toolbar from '@mui/material/Toolbar'
import FilterListIcon from '@mui/icons-material/FilterList'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'

import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import Autocomplete from '@mui/material/Autocomplete'
import Select, { SelectChangeEvent } from '@mui/material/Select'

import Typography from '@mui/material/Typography'

import { EIPSummary, NATGateway } from '../../eip'
import { toRepIndicator, useEIPTable } from './Data'
import {
  useEIPFilter,
  AutoCompleteOption,
  AddressFilter,
  NatGatewayFilter,
  MTAHostnameFilter,
  SubnetIDFilter,
  NoSubnetsFilter,
  IPRangeFilter,
  IPRepFilter
} from './Filter'
import { useSort } from './Sort'

import { isPartialIP, isIPRange, unusedBits, expandToIPAddr, parseIPRange } from './IPRange'

import CopyToClipboard from '../CopyToClipboard'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import IconButton from '@mui/material/IconButton'

const StyledBackdrop = styled(Backdrop)(() => ({
  backgroundColor: 'rgba(0, 0, 0, 0.2)'
}))

const StyledToolbar = styled(Toolbar)(() => ({
  justifyContent: 'space-between'
}))

const StyledSearchBarAutocomplete = styled(Autocomplete)(() => ({
  marginTop: '20px',
  marginBottom: '20px',
  width: '35%'
}))

const StyledProdIPFilterFormControlLabel = styled(FormControlLabel)(() => ({
  paddingRight: '16px'
}))

const StyledIPRepFilterFormControlLabel = styled(FormControlLabel)(() => ({
  paddingLeft: '16px',
  paddingRight: '10px'
}))

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.info.light
}))

const StyledExtLinkIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1)
}))

interface Column {
  header: string;
  align?: 'left' | 'center' | 'right';
}

export interface Columns {
  address: Column;
  gatewayID: Column;
  mtaHostname: Column;
  subnetID: Column;
  reputation: Column;
}

const columns: Columns = {
  address: { header: 'EIP Address' },
  gatewayID: { header: 'NAT Gateway', align: 'right' },
  mtaHostname: { header: 'MTA hostname' },
  subnetID: { header: 'Subnet ID' },
  reputation: { header: 'Reputation', align: 'center' }
}

interface Cell {
  label: string;
  link?: string;
  externalLink?: string;
  copyable?: boolean;
}

export type EIPTableRow = Map<keyof Columns, Cell | undefined>

const resolveSubnetsCell = (gateway: NATGateway): Cell => {
  if (gateway.subnet_ids === undefined || gateway.subnet_ids.length === 0) {
    return {
      label: 'No subnet',
      copyable: false
    }
  }

  if (gateway.subnet_ids.length > 1) {
    return {
      label: `${gateway.subnet_ids.length} subnets`,
      externalLink: gateway.subnets_link,
      copyable: false
    }
  }

  return {
    label: gateway.subnet_ids[0],
    externalLink: gateway.subnets_link
  }
}

export const tableDataFromSummaries = (summaries: EIPSummary[]): EIPTableRow[] => summaries.map(summary => {
  const row = new Map() as EIPTableRow

  const address: Cell = {
    label: summary.address,
    link: `eips/${summary.address}`,
    externalLink: summary.address_link
  }

  const gatewayID: Cell | undefined = summary.nat_gateway
    ? {
      label: summary.nat_gateway.id,
      externalLink: summary.nat_gateway.link
    }
    : undefined

  const mtaHostname: Cell | undefined = summary.nat_gateway?.mta_hostname
    ? {
      label: summary.nat_gateway.mta_hostname,
      externalLink: summary.nat_gateway.mta_link
    }
    : undefined

  const subnetID: Cell | undefined = summary.nat_gateway ? resolveSubnetsCell(summary.nat_gateway) : undefined

  const reputation: Cell = {
    label: toRepIndicator(summary.listed_status),
    copyable: false
  }

  row.set('address', address)
  row.set('gatewayID', gatewayID)
  row.set('mtaHostname', mtaHostname)
  row.set('subnetID', subnetID)
  row.set('reputation', reputation)

  return row
})

export interface EIPCellProps {
  cell?: Cell;
  align?: 'left' | 'center' | 'right';
  copyable: boolean;
}

export const EIPCell = ({ cell, align, copyable }: EIPCellProps): JSX.Element => {
  return (
    <TableCell align={align ?? 'left'}>
      {cell &&
        <Typography>
          {cell.link ? <StyledLink href={cell.link}>{cell.label}</StyledLink> : cell.label}
          {copyable && <CopyToClipboard copyText={cell.label} />}
          {cell.externalLink !== undefined &&
            <StyledLink href={cell.externalLink} target='_blank' rel='noopener noreferrer'>
              <StyledExtLinkIconButton aria-label="external link">
                <OpenInNewIcon />
              </StyledExtLinkIconButton>
            </StyledLink>}
        </Typography>}
    </TableCell>
  )
}

// Combines the type and label into a unique key to ensure the option isn't duplicated.
const addIfNotPresent = (mp: Map<string, AutoCompleteOption>, ...filters: AutoCompleteOption[]): void => {
  filters.forEach(flts => mp.set(flts.type + flts.label, flts))
}

const ipRangeIsApplicable = (fl: IPRangeFilter, summaries: EIPSummary[]): boolean => {
  return summaries.filter(s => fl.predicate(s)).length > 0
}

const createAllFilters = (currentSearchValue: string): IPRangeFilter[] => {
  if (isIPRange(currentSearchValue)) {
    const { networkPrefix, prefixLength } = parseIPRange(currentSearchValue)

    return [new IPRangeFilter(networkPrefix, prefixLength)]
  } else if (isPartialIP(currentSearchValue)) {
    const expandedIP = expandToIPAddr(currentSearchValue)

    return [new IPRangeFilter(expandedIP, 32 - unusedBits(expandedIP))]
  }

  return []
}

const generateIPRangeFilters = (currentSearchValue: string, summaries: EIPSummary[]): AutoCompleteOption[] =>
  createAllFilters(currentSearchValue).filter(f => ipRangeIsApplicable(f, summaries))

const toAutocompleteOptions = (summaries: EIPSummary[], currentSearchValue: string, searchValues: AutoCompleteOption[]): AutoCompleteOption[] => {
  const options: Map<string, AutoCompleteOption> = new Map<string, AutoCompleteOption>()

  for (const s of summaries) {
    addIfNotPresent(options, new AddressFilter(s.address))

    if (s.nat_gateway) {
      addIfNotPresent(options, new NatGatewayFilter(s.nat_gateway.id))

      if (s.nat_gateway.mta_hostname) {
        addIfNotPresent(options, new MTAHostnameFilter(s.nat_gateway.mta_hostname))
      }

      if (s.nat_gateway.subnet_ids) {
        addIfNotPresent(options, ...(s.nat_gateway.subnet_ids.map(id => new SubnetIDFilter(id))))
      }
    }
  }

  if (!summaries.every(s => (s.nat_gateway?.subnet_ids?.length ?? 0) > 0)) {
    addIfNotPresent(options, new NoSubnetsFilter())
  }

  addIfNotPresent(options, ...generateIPRangeFilters(currentSearchValue, summaries))

  addIfNotPresent(options, ...searchValues)

  return Array.from(options.values()).sort((a, b) => -b.type.localeCompare(a.type))
}

export const EIPTable = (): JSX.Element => {
  const { eipSummaries, loading, errorMessage } = useEIPTable()

  const {
    filteredEIPSummaries,
    showNonProdEIP,
    filterMenuAnchor,
    ipRepFilter,
    searchValues,
    searchInputValue,
    setSearchInputValue,
    setSearchValues,
    setIPRepFilter,
    openFilterMenu,
    closeFilterMenu,
    handleChangeFilterNonProdEIP
  } = useEIPFilter(eipSummaries)

  const tableData = tableDataFromSummaries(filteredEIPSummaries ?? [])

  const autocompleteOptions = toAutocompleteOptions(filteredEIPSummaries ?? [], searchInputValue, searchValues)

  const {
    sortedTableData,
    updateSorting,
    order,
    orderBy
  } = useSort(tableData)

  return (
    <>
      {loading && (
        <StyledBackdrop open={loading}>
          <CircularProgress color="primary" />
        </StyledBackdrop>
      )}
      {!loading && errorMessage === undefined && sortedTableData && (
        <Paper elevation={3}>
          <StyledToolbar>
            <StyledSearchBarAutocomplete
              id='search'
              multiple
              options={autocompleteOptions}
              value={searchValues}
              onChange={(_, newValues: unknown) => setSearchValues(newValues as AutoCompleteOption[])}
              getOptionLabel={(option: unknown) => (option as AutoCompleteOption).label}
              isOptionEqualToValue={(opt: unknown, val: unknown) => (opt as AutoCompleteOption).type === (val as AutoCompleteOption).type && (opt as AutoCompleteOption).label === (val as AutoCompleteOption).label}
              groupBy={(option: unknown) => (option as AutoCompleteOption).type}
              inputValue={searchInputValue}
              onInputChange={(_, newInputValue) => setSearchInputValue(newInputValue)}
              renderInput={(params) =>
                <TextField
                  {...params}
                  placeholder="Search..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start"><SearchIcon /></InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  } as InputProps}
                />
              }
            />
            <Button
              aria-label="filter list"
              onClick={openFilterMenu}
              endIcon={<FilterListIcon />}
            >Filters</Button>
            <Menu
              keepMounted
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={closeFilterMenu}
            >
              <MenuItem>
                <StyledProdIPFilterFormControlLabel
                  control={<Switch checked={showNonProdEIP} onChange={handleChangeFilterNonProdEIP} />}
                  labelPlacement="start"
                  label="Show non-production EIPs"
                />
              </MenuItem>
              <StyledIPRepFilterFormControlLabel
                control={
                  <Select
                    value={ipRepFilter}
                    onChange={(event: SelectChangeEvent<unknown>) => setIPRepFilter(event.target.value as IPRepFilter)}
                  >
                    <MenuItem value='All' > ✅ + ⚠️ + ❌ </MenuItem>
                    <MenuItem value='Good' > ✅ </MenuItem>
                    <MenuItem value='Bad'> ⚠️ + ❌ </MenuItem>
                  </Select>
                }
                labelPlacement="start"
                label="Show reputation"
              />
            </Menu>
          </StyledToolbar>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {Object.entries(columns).map(([id, col]) => (
                    <TableCell key={id} align={(col as Column).align ?? 'left'}>
                      <TableSortLabel
                        active={orderBy === id}
                        direction={orderBy === id ? order : 'asc'}
                        onClick={(): void => updateSorting(id as keyof Columns)}
                      >
                        <Typography variant="subtitle2">{(col as Column).header}</Typography>
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTableData.map((row) => (
                  <TableRow key={row.get('address')?.label}>
                    {Array.from(row.entries()).map(([id, columnCell]) => (
                      <EIPCell
                        key={id}
                        cell={columnCell}
                        align={columns[id].align ?? 'left'}
                        copyable={columnCell?.copyable ?? true}
                      />
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {errorMessage}
    </>
  )
}
